"use client";

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";

interface Modifier {
  id: string;
  name: string;
  price: number;
  isDefault: boolean;
}

interface ModifierGroup {
  id: string;
  name: string;
  type: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  modifiers: Modifier[];
}

interface ModifierModalItem {
  name: string;
  price: number;
  modifierGroups: ModifierGroup[];
}

interface SelectedModifier {
  modifierId: string;
  name: string;
  price: number;
}

interface ModifierModalProps {
  item: ModifierModalItem;
  onConfirm: (
    selectedModifiers: SelectedModifier[],
    quantity: number,
    notes: string,
  ) => void;
  onClose: () => void;
}

export function ModifierModal({ item, onConfirm, onClose }: ModifierModalProps) {
  const [selectedModifiers, setSelectedModifiers] = useState<Record<string, Set<string>>>(() => {
    const initial: Record<string, Set<string>> = {};
    for (const group of item.modifierGroups) {
      const defaults = group.modifiers
        .filter((m) => m.isDefault)
        .map((m) => m.id);
      initial[group.id] = new Set(defaults);
    }
    return initial;
  });
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  const modifierTotal = useMemo(() => {
    let total = 0;
    for (const group of item.modifierGroups) {
      const selectedIds = selectedModifiers[group.id] ?? new Set();
      for (const mod of group.modifiers) {
        if (selectedIds.has(mod.id)) {
          total += mod.price;
        }
      }
    }
    return total;
  }, [item.modifierGroups, selectedModifiers]);

  const lineTotal = (item.price + modifierTotal) * quantity;

  const allRequiredSelected = useMemo(() => {
    return item.modifierGroups
      .filter((g) => g.isRequired)
      .every((g) => {
        const count = selectedModifiers[g.id]?.size ?? 0;
        return count >= g.minSelect;
      });
  }, [item.modifierGroups, selectedModifiers]);

  function handleToggle(group: ModifierGroup, modifierId: string) {
    setSelectedModifiers((prev) => {
      const current = new Set(prev[group.id] ?? []);

      if (group.maxSelect === 1) {
        // Radio behavior
        const newSet = new Set<string>();
        if (!current.has(modifierId)) {
          newSet.add(modifierId);
        }
        return { ...prev, [group.id]: newSet };
      }

      // Checkbox behavior
      if (current.has(modifierId)) {
        current.delete(modifierId);
      } else if (current.size < group.maxSelect) {
        current.add(modifierId);
      }

      return { ...prev, [group.id]: current };
    });
  }

  function handleConfirm() {
    const result: SelectedModifier[] = [];
    for (const group of item.modifierGroups) {
      const selectedIds = selectedModifiers[group.id] ?? new Set();
      for (const mod of group.modifiers) {
        if (selectedIds.has(mod.id)) {
          result.push({ modifierId: mod.id, name: mod.name, price: mod.price });
        }
      }
    }
    onConfirm(result, quantity, notes);
  }

  return (
    <Modal isOpen onClose={onClose} title={item.name} size="lg">
      <div className="space-y-5">
        {/* Base price */}
        <p className="text-sm text-muted">
          Base price: <span className="text-white font-medium">{formatCurrency(item.price)}</span>
        </p>

        {/* Modifier groups */}
        {item.modifierGroups.map((group) => {
          const selectedIds = selectedModifiers[group.id] ?? new Set();
          const isRadio = group.maxSelect === 1;

          return (
            <div key={group.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">{group.name}</h4>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-medium",
                    group.isRequired
                      ? "bg-amber-500/10 text-amber-400"
                      : "bg-surface-border text-dim",
                  )}
                >
                  {group.isRequired ? "Required" : "Optional"}
                </span>
              </div>

              <div className="space-y-1">
                {group.modifiers.map((mod) => {
                  const isSelected = selectedIds.has(mod.id);
                  return (
                    <button
                      key={mod.id}
                      onClick={() => handleToggle(group, mod.id)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        isSelected
                          ? "bg-white/5 border border-white/10"
                          : "border border-transparent hover:bg-surface-border",
                      )}
                    >
                      {/* Radio or checkbox indicator */}
                      <span
                        className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center border transition-colors",
                          isRadio ? "rounded-full" : "rounded",
                          isSelected
                            ? "border-white bg-white"
                            : "border-text-dim",
                        )}
                      >
                        {isSelected && (
                          isRadio ? (
                            <span className="h-2 w-2 rounded-full bg-black" />
                          ) : (
                            <svg className="h-3 w-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )
                        )}
                      </span>

                      <span className="flex-1 text-left text-white/80">{mod.name}</span>

                      {mod.price > 0 && (
                        <span className="text-dim">+{formatCurrency(mod.price)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Notes */}
        <Textarea
          label="Special instructions"
          placeholder="Any special requests..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {/* Quantity selector */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-white/80">Quantity</span>
          <div className="inline-flex items-center rounded-lg border border-surface-border-2 bg-surface-border">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-l-lg text-muted transition-colors",
                "hover:bg-white/10 hover:text-white",
                "disabled:opacity-40 disabled:cursor-not-allowed",
              )}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
              </svg>
            </button>
            <span className="flex h-9 w-10 items-center justify-center text-sm font-medium text-white">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="flex h-9 w-9 items-center justify-center rounded-r-lg text-muted transition-colors hover:bg-white/10 hover:text-white"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2 border-t border-surface-border">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!allRequiredSelected}
            className="flex-[2]"
          >
            Add to Order &middot; {formatCurrency(lineTotal)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
